import { AlterableBusinessHeader } from '@/coreDomainsModule/entities/businessExcelInfo';
import { REQUIRE_ALTERABLE_HEADERS } from '@/registerInputModule/constants';
import { AlterableHeader } from '@/coreDomainsModule/entities/inputExcelInfo';
import { RegisterOrderExcelInfos } from '../../../coreDomainsModule/entities/InputOrderExcelInfo';

/**
 * 発注エクセル登録設定の保存方法
 */
export type OrderExcelInfoSaveMode = 'replace' | 'append';

/**
 * 取引情報紐付け
 * @param linkedBusinessHeaders - 紐付けのヘッダー
 * @param alternateHeaderInfos - 読み替え可能なヘッダー情報
 * @param inlinedYearMonthInfo - 年月情報の取得パターン（インライン）
 * @param inHeaderYearMonthInfo - 年月情報の取得パターン（ヘッダー）
 * @param orderExcelInfos - 発注エクセル登録設定一覧
 * @param orderExcelInfoSaveMode - 発注エクセル登録設定の保存方法
 * @param orderExcelInfoReplaceTargetId - 置き換える発注エクセル登録設定ID
 */
export interface LinkingInput {
  alternateHeaderInfos: AlterableHeaderInfo[];
  inlinedYearMonthInfo: SelectYearMonthPatternInlined;
  inHeaderYearMonthInfo: SelectYearMonthPatternInHeader;
  orderExcelInfos: RegisterOrderExcelInfos;
  orderExcelInfoSaveMode: OrderExcelInfoSaveMode;
  orderExcelInfoReplaceTargetId: string;
}

/**
 * エラーの種類
 */
type ValidateError = 'NotSelected' | 'None';

/**
 * 紐付けのヘッダー
 * @param displayName - 表示名
 * @param key - キー
 * @param value - 選択された値を保持する
 */
export interface LinkingInputHeader {
  displayName: string;
  key: AlterableBusinessHeader;
  value?: string;
  required: boolean;
  validateError: ValidateError;
}

/**
 * 読み替え可能なヘッダーのうち、登録が必須であるヘッダー一覧
 */
export type RequireAlterableHeader = (typeof REQUIRE_ALTERABLE_HEADERS)[number];

/**
 * 高速判定用 Set（実行時データ）
 */
const requiredSet = new Set<RequireAlterableHeader>(REQUIRE_ALTERABLE_HEADERS);

/**
 * RequireAlterableHeaderの型ガード
 * @param header - ヘッダー
 * @returns - ヘッダーがRequireAlterableHeaderであるかどうか
 */
export const isRequireAlterableHeader = (header: AlterableHeader): header is RequireAlterableHeader => {
  return requiredSet.has(header as RequireAlterableHeader);
};

/**
 * 読み替え可能なヘッダー情報
 * @param header - ヘッダー
 * @param value - 値
 * @param unused - 登録しないヘッダーかどうか(undefinedの場合は登録する/しないは調整できない)
 * @param validateError - エラーの種類
 */
export interface AlterableHeaderInfo {
  header: AlterableHeader;
  value: string;
  unused: boolean | undefined;
  validateError: boolean;
}

/**
 * 年月情報の取得パターン（インライン）
 * @param headerName - 取得するヘッダー名
 */
export type SelectYearMonthPatternInlined = {
  headerName: string;
  validateError: SelectYearMonthPatternError;
};

/**
 * 年月情報の取得パターン（ヘッダー）
 * @param startColumnNumber - 開始列番号
 * @param endColumnNumber - 終了列番号
 * @param startYear - 開始年
 * @param startMonth - 開始月
 * @param endYear - 終了年
 * @param endMonth - 終了月
 */
export type SelectYearMonthPatternInHeader = {
  startColumnNumber: {
    raw: string;
    validateError: SelectYearMonthPatternError;
  };
  endColumnNumber: {
    raw: string;
    validateError: SelectYearMonthPatternError;
  };
  start: {
    raw: string;
    validateError: SelectYearMonthPatternError;
  };
  end: {
    raw: string;
    validateError: SelectYearMonthPatternError;
  };
};

/**
 * 年月情報の取得パターンのエラー
 * @param cellPattern - セルの形式
 * @param sideBySideCell - セルが隣接しているかどうか
 * @param yearMonth - 年月
 * @param sideBySideYearMonth - 年月が隣接しているかどうか
 * @param notEmpty - 空でない
 * @param none - なし
 */
export type SelectYearMonthPatternError = 'cellPattern' | 'sideBySideCell' | 'yearMonth' | 'sideBySideYearMonth' | 'notEmpty' | 'none';
